import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { fmtNumber } from "../lib/safeFormat";
import { Input } from "../components/ui/input";
import { Search, Star, DollarSign, Percent, Home, AlertTriangle, RefreshCw, Lock, ArrowRight, MapPin, X } from "lucide-react";
import { formatCurrency, PropertyTypeIcon, buildPropertyAltText } from "../lib/helpers";
import { PropertyImage } from "../components/PropertyImage";
import { DealScoreGauge } from "../components/DealScoreGauge";
import { HomeNav } from "../components/home/HomeNav";
import { getDealInsight, getTrustNotes, meetsQualityThreshold } from "../lib/dealInsights";
import SEOHead from "../components/SEOHead";
import Footer from "../components/Footer";
import RelatedPages from "../components/RelatedPages";
import DealOfTheDay from "../components/DealOfTheDay";
import DealComparison from "../components/DealComparison";
import LocationAutocomplete from "../components/LocationAutocomplete";
import DealAlertToggle from "../components/DealAlertToggle";
import DealAlertPrompt from "../components/DealAlertPrompt";
import UpdatedBadge from "../components/UpdatedBadge";
import AnalysisModal from "../components/AnalysisModal";
import UpgradeModal from "../components/UpgradeModal";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

export default function TopDealsPage({ user, isAuthenticated }) {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('deal_score');
  const [activeSection, setActiveSection] = useState('all');
  const [isPaid, setIsPaid] = useState(false);
  const [teaserCount, setTeaserCount] = useState(3);
  const [locationFilter, setLocationFilter] = useState("");
  const [activeLocation, setActiveLocation] = useState("");
  const [locationLoading, setLocationLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [freshness, setFreshness] = useState(null);
  const [newIds, setNewIds] = useState(null); // Set of property IDs first seen in last 24h

  // ── Inline AnalysisModal state — replaces the old /dashboard navigation ──
  const [subscription, setSubscription] = useState(null);
  const [analysisParams, setAnalysisParams] = useState({});
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [savedPropertyIds, setSavedPropertyIds] = useState(new Set());
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState(null);

  // Open analysis IN-PLACE (was: navigate to /dashboard?property=)
  const handleOpenAnalysis = useCallback((property) => {
    if (!property) return;
    setSelectedProperty(property);
    setSelectedAnalysis(null);
    setIsModalOpen(true);
    setAnalysisLoading(true);
    axios
      .get(`${API}/properties/${property.id}/analysis`, { params: analysisParams })
      .then((r) => setSelectedAnalysis(r.data))
      .catch(() => {})
      .finally(() => setAnalysisLoading(false));
  }, [analysisParams]);

  const handleSaveToggle = useCallback((propertyId, willSave) => {
    setSavedPropertyIds((prev) => {
      const next = new Set(prev);
      if (willSave) next.add(propertyId);
      else next.delete(propertyId);
      return next;
    });
  }, []);

  const fetchDeals = useCallback((loc = "") => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "50" });
    if (loc) params.append("location", loc);
    axios.get(`${API}/top-deals?${params.toString()}`).then(r => {
      setDeals(r.data.deals || []);
      setSections(r.data.sections || {});
      setActiveLocation(loc);
    }).catch(console.error).finally(() => {
      setLoading(false);
      setLocationLoading(false);
    });
  }, []);

  useEffect(() => {
    fetchDeals();
    axios.get(`${API}/paywall-config`).then(r => {
      setTeaserCount(r.data.teaser_count || 3);
    }).catch(() => {});
    // Real freshness timestamp from the top_deals_cache — powers the
    // visible "Updated" badge that matches the sitemap <lastmod>.
    axios.get(`${API}/top-deals/updated-at`).then(r => {
      setUpdatedAt(r.data?.cached_at || null);
    }).catch(() => {});
    // "X new deals in last 24h" summary — tiny read, no heavy computation.
    axios.get(`${API}/top-deals/freshness`).then(r => {
      setFreshness(r.data || null);
    }).catch(() => {});
    // Which cards are actually new (first_seen_at within 24h). Tiny ID list
    // so the "New" badges match the aggregate count the user sees above.
    axios.get(`${API}/top-deals/new-ids`).then(r => {
      setNewIds(new Set((r.data?.ids || []).map(String)));
    }).catch(() => {});
  }, [fetchDeals]);

  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API}/subscription`).then(r => {
        // Use is_paid (covers both Pro AND Elite) — Elite users have is_pro=false
        setIsPaid(r.data?.is_paid || false);
        setSubscription(r.data || null);
      }).catch(() => {});
      // Pull saved-property IDs so the heart icon reflects state when the
      // AnalysisModal opens here (was previously handled by DashboardPage).
      axios.get(`${API}/saved-properties`).then(r => {
        const arr = Array.isArray(r.data) ? r.data : (r.data?.saved || []);
        setSavedPropertyIds(new Set(arr.map((s) => s?.property?.id).filter(Boolean)));
      }).catch(() => {});
    }
  }, [isAuthenticated]);

  const sorted = useMemo(() => {
    const d = [...deals];
    if (sortBy === 'deal_score') d.sort((a, b) => b.deal_score - a.deal_score);
    else if (sortBy === 'cash_flow') d.sort((a, b) => b.cash_flow_monthly - a.cash_flow_monthly);
    else if (sortBy === 'cap_rate') d.sort((a, b) => b.cap_rate - a.cap_rate);
    return d;
  }, [deals, sortBy]);

  // Get filtered deals based on active section
  const filteredDeals = useMemo(() => {
    if (activeSection === 'all') return sorted;
    if (activeSection === 'cash_flow') return sections.top_cash_flow || [];
    if (activeSection === 'cap_rate') return sections.top_cap_rate || [];
    if (activeSection === 'under300k') return sections.best_under_300k || [];
    if (activeSection === 'distressed') return sections.distressed_deals || [];
    return sorted;
  }, [activeSection, sorted, sections]);

  const DealCard = ({ deal, rank }) => {
    const insight = getDealInsight(deal);
    const trustNotes = getTrustNotes(deal);
    const isNew = newIds && newIds.has(String(deal.property?.id ?? ""));
    return (
    <Card className="bg-card border-border overflow-hidden cursor-pointer card-hover" onClick={() => handleOpenAnalysis(deal.property)} data-testid={`top-deal-card-${deal.property?.id}`}>
      <div className="relative">
        <PropertyImage
          property={deal.property}
          alt={buildPropertyAltText(deal.property, deal)}
          imgClassName="w-full h-44 object-cover"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          <Badge className="bg-black/70 text-white border-0 text-[10px]">
            <PropertyTypeIcon type={deal.property.property_type} />
            <span className="ml-1 capitalize">{deal.property.property_type.replace('_', ' ')}</span>
          </Badge>
          {isNew && (
            <Badge
              className="bg-emerald-500/90 text-white border-0 text-[10px] font-semibold"
              data-testid={`new-badge-${deal.property?.id ?? rank}`}
              title="First seen in the last 24 hours"
            >
              New
            </Badge>
          )}
        </div>
        {rank && <div className="absolute bottom-3 right-3"><Badge className="bg-black/70 text-white border-0 font-mono">#{rank}</Badge></div>}
        <div className="absolute bottom-3 left-3"><span className="font-mono text-xl font-bold text-white drop-shadow-lg">{formatCurrency(deal.property.price)}</span></div>
      </div>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <h3 className="font-semibold text-sm truncate">{deal.property.address}</h3>
            <p className="text-xs text-muted-foreground">{deal.property.city}, {deal.property.state} {deal.property.zip_code}</p>
          </div>
          <DealScoreGauge score={deal.deal_score} size="sm" tone="app" className="flex-shrink-0" />
        </div>

        {/* Primary insight */}
        {insight && (
          <div className="bg-emerald-500/8 border border-emerald-500/15 rounded px-2.5 py-1.5 mb-3">
            <p className="text-xs font-semibold text-emerald-500 leading-snug">{insight.text}</p>
          </div>
        )}

        <div className="flex gap-3 text-xs text-muted-foreground mb-3">
          <span>{deal.property.bedrooms} beds</span>
          <span>{deal.property.bathrooms} baths</span>
          <span>{(deal.property.sqft || 0).toLocaleString()} sqft</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><span className="text-[10px] text-muted-foreground uppercase">Est. Rent</span><p className="font-mono text-sm font-semibold">{formatCurrency(deal.estimated_monthly_rent)}/mo</p></div>
          <div><span className="text-[10px] text-muted-foreground uppercase">Cash Flow</span><p className={`font-mono text-sm font-semibold ${deal.cash_flow_monthly >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(deal.cash_flow_monthly)}/mo</p></div>
          <div><span className="text-[10px] text-muted-foreground uppercase">Cap Rate</span><p className="font-mono text-sm font-semibold">{fmtNumber(deal.cap_rate)}%</p></div>
          <div><span className="text-[10px] text-muted-foreground uppercase">Mortgage</span><p className="font-mono text-sm font-semibold text-red-400">{formatCurrency(deal.monthly_mortgage)}/mo</p></div>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex gap-2">
            {deal.one_percent_rule && <Badge variant="secondary" className="text-[10px] bg-emerald-500/10 text-emerald-500 border-0">1% Rule</Badge>}
            {deal.fifty_percent_rule && <Badge variant="secondary" className="text-[10px] bg-emerald-500/10 text-emerald-500 border-0">50% Rule</Badge>}
          </div>
        </div>
        {/* Trust note */}
        {trustNotes.length > 0 && (
          <p className="text-[9px] text-muted-foreground/60 mt-2 flex items-center gap-1">
            <AlertTriangle className="w-2.5 h-2.5 shrink-0" />{trustNotes[0]}
          </p>
        )}
        {/* Edit numbers hint */}
        <p className="text-[10px] text-muted-foreground/60 text-center mt-3 pt-2 border-t border-dashed border-border/40 hover:text-emerald-500 transition-colors" data-testid="edit-numbers-hint">
          Based on averages — <span className="underline underline-offset-2">click to customize</span>
        </p>
      </CardContent>
    </Card>
    );
  };

  const SeizedCard = ({ prop }) => (
    <Card className="bg-card border-border cursor-pointer card-hover" onClick={() => navigate('/dashboard')}>
      <CardContent className="p-4">
        <Badge className="bg-orange-500/15 text-orange-500 border-orange-500/30 mb-2">Seized — U.S. Treasury</Badge>
        <p className="font-semibold text-sm">{prop.address}</p>
        <p className="text-xs text-muted-foreground">{prop.city}, {prop.state} {prop.zip_code}</p>
        {prop.starting_bid && <p className="font-mono text-lg font-bold mt-2">{formatCurrency(prop.starting_bid)}</p>}
        {prop.auction_date && <p className="text-xs text-orange-400 mt-1">Auction: {prop.auction_date}</p>}
        <div className="flex gap-3 text-xs text-muted-foreground mt-2">
          {prop.bedrooms > 0 && <span>{prop.bedrooms} beds</span>}
          {prop.bathrooms > 0 && <span>{prop.bathrooms} baths</span>}
          {prop.sqft > 0 && <span>{prop.sqft.toLocaleString()} sqft</span>}
        </div>
      </CardContent>
    </Card>
  );

  const sectionTabs = [
    { key: 'all', label: 'All Deals', icon: Star },
    { key: 'cash_flow', label: 'Top Cash Flow', icon: DollarSign },
    { key: 'cap_rate', label: 'Top Cap Rate', icon: Percent },
    { key: 'under300k', label: 'Under $300K', icon: Home },
    { key: 'distressed', label: 'Distressed', icon: AlertTriangle },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Top Investment Property Deals Today | RealixData"
        description="Browse today's highest-ranked rental investment properties across the U.S. Sorted by deal score, cash flow, and cap rate. Updated daily."
        canonical="/top-deals"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Top Investment Property Deals Today",
          "description": "Today's highest-ranked rental investment properties across the U.S., sorted by deal score, cash flow, and cap rate.",
          "url": "https://realixdata.com/top-deals",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://realixdata.com/" },
              { "@type": "ListItem", "position": 2, "name": "Top Deals", "item": "https://realixdata.com/top-deals" }
            ]
          }
        }}
      />
      <HomeNav isAuthenticated={isAuthenticated} />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 sm:py-10 pb-28 md:pb-10">
        {/* SEO-friendly intro content */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1" data-testid="top-deals-title">Top Deals Today</h1>
          <p className="text-muted-foreground text-sm sm:text-base mb-3">The best investment opportunities ranked by quality</p>
          {(updatedAt || (freshness && freshness.new_deals_24h > 0)) && (
            <div className="flex flex-col gap-1 border-l-2 border-border pl-3 py-0.5 mb-4" data-testid="top-deals-meta-block">
              {updatedAt && (
                <UpdatedBadge
                  cachedAt={updatedAt}
                  tone="emerald"
                  testId="top-deals-updated-at"
                  suffix={deals.length > 0 ? ` · ${deals.length} active deals` : ""}
                />
              )}
              {freshness && freshness.new_deals_24h > 0 && (
                <p className="text-xs text-muted-foreground" data-testid="top-deals-freshness-line">
                  <span className="font-semibold text-foreground">{freshness.new_deals_24h} new deal{freshness.new_deals_24h === 1 ? "" : "s"}</span>
                  {" "}added in the last 24h
                  {freshness.profitable_deals_count > 0 ? ` · ${freshness.profitable_deals_count} profitable` : ""}
                </p>
              )}
            </div>
          )}
          <div className="mb-3">
            <DealAlertToggle category="top-deals" isAuthenticated={isAuthenticated} />
          </div>
          <div className="mb-5">
            <DealAlertPrompt
              category="top-deals"
              isAuthenticated={isAuthenticated}
              newDeals24h={freshness?.new_deals_24h || 0}
              visibleDealIds={(deals || []).map(d => d?.property?.id).filter(Boolean).slice(0, 20)}
            />
          </div>
          <p className="text-sm text-muted-foreground max-w-3xl leading-relaxed">
            RealixData ranks hundreds of rental properties daily using our proprietary deal scoring algorithm.
            Each property is analyzed for cash flow, cap rate, ROI, and BRRRR potential using live market data
            from Zillow and local MLS feeds. Properties that meet the 1% rule, generate positive
            cash flow, and sit in strong rental markets rise to the top. Updated every 12 hours.
          </p>
          <button
            onClick={() => navigate("/faq/markets-we-analyze")}
            className="text-xs text-emerald-500 hover:text-emerald-400 mt-2 flex items-center gap-1 transition-colors"
            data-testid="markets-we-analyze-link"
          >
            <MapPin className="w-3 h-3" /> See the 100 markets we analyze <ArrowRight className="w-3 h-3" />
          </button>
        </div>

        {/* Location Filter */}
        <div className="mb-6" data-testid="top-deals-location-filter">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                <div className="flex-1 w-full">
                  <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">
                    <MapPin className="w-3 h-3 inline mr-1" />Find Deals In
                  </label>
                  <LocationAutocomplete
                    value={locationFilter}
                    onChange={setLocationFilter}
                    onSelect={(display) => setLocationFilter(display)}
                    placeholder="State, city, or zip code (e.g., FL, Houston TX, 33605)"
                  />
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    onClick={() => { setLocationLoading(true); fetchDeals(locationFilter); }}
                    disabled={locationLoading || !locationFilter.trim()}
                    className="flex-1 sm:flex-none"
                    data-testid="top-deals-search-btn"
                  >
                    {locationLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Search Market
                  </Button>
                  {activeLocation && (
                    <Button
                      variant="outline"
                      onClick={() => { setLocationFilter(""); fetchDeals(""); }}
                      className="flex-none"
                      data-testid="top-deals-clear-btn"
                    >
                      <X className="w-4 h-4 mr-1" />All Markets
                    </Button>
                  )}
                </div>
              </div>
              {activeLocation && (
                <p className="text-xs text-emerald-500 mt-2 flex items-center gap-1" data-testid="active-location-label">
                  <MapPin className="w-3 h-3" />
                  Showing deals in: <span className="font-semibold">{activeLocation}</span>
                  <span className="text-muted-foreground ml-1">({deals.length} found)</span>
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Deal of the Day */}
        <div className="mb-8" data-testid="top-deals-dotd">
          <DealOfTheDay variant="compact" />
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div></div>
          {activeSection === 'all' && (
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort deals by" className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground" data-testid="top-deals-sort">
              <option value="deal_score">Deal Score</option>
              <option value="cash_flow">Cash Flow</option>
              <option value="cap_rate">Cap Rate</option>
            </select>
          )}
        </div>
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="section-tabs">
          {sectionTabs.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setActiveSection(key)} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeSection === key ? 'bg-emerald-600 text-white' : 'bg-secondary/50 text-muted-foreground hover:text-foreground border border-border'}`} data-testid={`section-tab-${key}`}>
              <Icon className="w-3.5 h-3.5" />{label}
            </button>
          ))}
        </div>

        {/* Blur overlay for non-Pro users */}
        {!isPaid && !loading && deals.length > 0 && (
          <div className="relative" data-testid="top-deals-paywall">
            {activeSection === 'distressed' ? (
              <>
                <p className="text-sm text-muted-foreground mb-4">Foreclosures, auctions, and U.S. Treasury seized properties.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-0">
                  {(sections.distressed_deals || []).slice(0, teaserCount).map((prop, i) => <SeizedCard key={prop.sale_number || i} prop={prop} />)}
                </div>
              </>
            ) : (
              <>
                {activeSection !== 'all' && (
                  <p className="text-sm text-muted-foreground mb-4">
                    {activeSection === 'cash_flow' && 'Properties with the highest monthly cash flow after all expenses.'}
                    {activeSection === 'cap_rate' && 'Properties with the highest capitalization rate (NOI / Price).'}
                    {activeSection === 'under300k' && 'Best investment-grade properties priced under $300,000.'}
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-0">
                  {filteredDeals.slice(0, teaserCount).map((deal, i) => <DealCard key={deal.property?.id || i} deal={deal} rank={i + 1} />)}
                </div>
              </>
            )}
            {/* Blurred section */}
            <div className="relative mt-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 blur-md select-none pointer-events-none" aria-hidden="true">
                {activeSection === 'distressed'
                  ? (sections.distressed_deals || []).slice(teaserCount, teaserCount + 6).map((prop, i) => <SeizedCard key={prop.sale_number || i} prop={prop} />)
                  : filteredDeals.slice(teaserCount, teaserCount + 6).map((deal, i) => <DealCard key={deal.property?.id || i} deal={deal} rank={i + teaserCount + 1} />)
                }
              </div>
              {/* CTA overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-sm rounded-xl" data-testid="top-deals-upgrade-overlay">
                <div className="text-center max-w-md px-6">
                  <div className="w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-7 h-7 text-amber-500" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Unlock All Top Deals</h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    {isAuthenticated
                      ? "Upgrade to Pro to see all top-ranked investment properties with full analysis data."
                      : "Sign in and upgrade to Pro to access all top-ranked investment deals."}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {!isAuthenticated && (
                      <Button variant="outline" onClick={() => navigate('/auth')} data-testid="top-deals-login-btn">
                        Sign In
                      </Button>
                    )}
                    <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => navigate('/billing')} data-testid="top-deals-upgrade-btn">
                      Upgrade to Pro <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Full content for Pro users */}
        {isPaid && !loading && (
          <>
            {/* Deal Comparison Tool */}
            <DealComparison
              deals={filteredDeals}
              onNavigate={(id) => {
                const deal = deals.find((d) => String(d?.property?.id) === String(id));
                if (deal?.property) handleOpenAnalysis(deal.property);
                else navigate(`/dashboard?property=${id}`);
              }}
            />

            {activeSection !== 'distressed' ? (
              <div>
                {activeSection !== 'all' && (
                  <p className="text-sm text-muted-foreground mb-4">
                    {activeSection === 'cash_flow' && 'Properties with the highest monthly cash flow after all expenses.'}
                    {activeSection === 'cap_rate' && 'Properties with the highest capitalization rate (NOI / Price).'}
                    {activeSection === 'under300k' && 'Best investment-grade properties priced under $300,000.'}
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="top-deals-grid">
                  {filteredDeals.length > 0
                    ? filteredDeals.map((deal, i) => <DealCard key={deal.property?.id || i} deal={deal} rank={i + 1} />)
                    : <p className="col-span-3 text-center text-muted-foreground py-12">No deals in this category right now.</p>
                  }
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground mb-4">Foreclosures, auctions, and U.S. Treasury seized properties.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {(sections.distressed_deals || []).length > 0
                    ? (sections.distressed_deals || []).map((prop, i) => <SeizedCard key={prop.sale_number || i} prop={prop} />)
                    : <p className="col-span-3 text-center text-muted-foreground py-12">No distressed properties available right now.</p>}
                </div>
              </div>
            )}
          </>
        )}

        {loading && (
          <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" /></div>
        )}
      </div>
      <RelatedPages currentPath="/top-deals" />
      <Footer variant="dark" />

      {/* Inline AnalysisModal — opens in place when a card is clicked */}
      <AnalysisModal
        property={selectedProperty}
        analysis={selectedAnalysis}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        params={analysisParams}
        onParamsChange={setAnalysisParams}
        isSaved={selectedProperty ? savedPropertyIds.has(selectedProperty.id) : false}
        onSaveToggle={handleSaveToggle}
        subscription={subscription}
        onUpgrade={(reason) => {
          setUpgradeReason(reason);
          setIsUpgradeOpen(true);
        }}
      />
      <UpgradeModal
        isOpen={isUpgradeOpen}
        onClose={() => setIsUpgradeOpen(false)}
        reason={upgradeReason}
      />
    </div>
  );
}
